import React, { memo, useEffect, useState } from "react";
import { Checkbox, Input } from "antd";
import Spinner from "../../../../../common/Spinner";
import {
  antdMessageError,
  antdMessageSuccess,
  url_to_store_by_reference,
} from "../../../../../../helpers/Utils";
import { have_product_reference } from "../../../../../../helpers/OrdersFunctionsGlobal";
import TableUi from "../../../../../Layout/TableUI";
import {
  ButtonSecondary,
  Error,
  InputSwitchComponent,
  TextInput,
} from "../../../../../globals";
import { useChecked } from "../../../../../../hooks";
import { IconDelete, IconRollBack } from "../../../../../Layout/Icons";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
} from "react-query";
import Axios from "axios";
import { useDispatch } from "react-redux";
import OrderRowReference from "../../../../../orders/accepted_orders/features/order_rows/OrderRowReference";

export const queryOrderRowsReturnClient = new QueryClient();

const OrderRowsReturn = ({
  order_rows,
  order_shop,
  order_id,
  total_shipping_tax_incl,
}) => {
  const dispatch = useDispatch();
  const {
    error,
    isLoading,
    data: order_returns = [],
    refetch,
  } = useQuery({
    queryKey: [
      `order_returns/for_order`,
      {
        order_id,
      },
    ],
    queryFn: async ({ queryKey }) => {
      const [key, { order_id }] = queryKey;

      const response = await Axios.get(`/api/v1/${key}?order_id=${order_id}`);
      return response.data;
    },
  });
  const {
    onChangeChecked,
    checkedIncluded,
    checked,
    checkedAll,
    setChecked,
    setCheckById,
  } = useChecked([]);
  const [returnType, setReturnType] = useState(null);

  const [value, setValue] = useState({
    ...order_rows.reduce(
      (acc, currentValue) => ({
        ...acc,
        [currentValue.id]: {
          qty: 0,
          quantity: +currentValue?.quantity,
          product_reference: currentValue.product_reference,
          product_name: currentValue.product_name,
          price: currentValue.unit_price_tax_incl,
          returns_count: currentValue.returns_count,
          used: 0,
        },
      }),
      {}
    ),
    delivery: {
      qty: 0,
      quantity: +total_shipping_tax_incl > 0 ? 1 : 0,
      product_reference: null,
      product_name: "Delivery",
      price: Number(total_shipping_tax_incl),
      returns_count: 0,
      used: 0,
    },
  });

  const [originalReferences, setOriginalReferences] = useState(
    order_rows.reduce(
      (acc, currentValue) => ({
        ...acc,
        [currentValue.id]: currentValue.product_reference,
      }),
      {}
    )
  );

  useEffect(() => {
    if (order_returns && !isLoading) {
      setValue((prev) => {
        let obj = { ...prev };
        Object.keys(obj).forEach((order_row_id) => {
          obj[order_row_id].used = 0;
        });
        order_returns.forEach((row) => {
          const row_key =
            row.order_row_id || row.title?.toLowerCase() || "delivery";
          if (obj[row_key]) {
            obj[row_key].used += row.qty;
            obj[row_key].status = row.status;
          }
          if (row.order_row_id === null && row.qty !== null) {
            obj[row_key].used = row.qty;
          }
        });
        return obj;
      });
    }
  }, [order_returns]);

  useEffect(() => {
    if (!isLoading) {
      dispatch({ type: "ORDER_ROW_RETURNS_RECALCULATE", payload: value });
    }
  }, [value]);

  const mutationCreate = useMutation({
    mutationFn: () =>
      Axios({
        url: `/api/v1/order_returns`,
        method: "POST",
        data: {
          status: "new",
          return_type: returnType,
          order_id: order_id,
          order_row_returns_attributes: Object.entries(value)
            .map(([order_row_id, values]) => {
              const originalReference = originalReferences[order_row_id];
              const currentReference = values.product_reference;
              return {
                order_row_id: order_row_id == "delivery" ? null : order_row_id,
                qty: values.qty - values.used,
                title: values.product_name,
                price: values.price,
                sku:
                  currentReference !== originalReference
                    ? currentReference
                    : undefined,
              };
            })
            .filter(
              ({ order_row_id, qty, title }) =>
                qty > 0 &&
                checkedIncluded(title == "Delivery" ? "delivery" : order_row_id)
            ),
        },
      }).then((res) => res.data),
    onSuccess: (response, variables) => {
      antdMessageSuccess();
      refetch();
      setChecked([]);
      setReturnType(null);
    },
    onError: (error) => {
      antdMessageError(error?.response?.data?.message);
    },
  });

  const mutationDelete = useMutation({
    mutationFn: (record_id) =>
      Axios({
        url: `/api/v1/order_row_returns/${record_id}`,
        method: "DELETE",
      }).then((res) => res.data),
    onSuccess: (_, variables) => {
      antdMessageSuccess();
      queryOrderRowsReturnClient.setQueryData(
        [
          `order_returns/for_order`,
          {
            order_id,
          },
        ],
        (oldData) => {
          return oldData.filter(({ id }) => id !== variables);
        }
      );
    },
    onError: (error) => {
      antdMessageError(error?.response?.data?.message);
    },
  });

  if (isLoading) {
    return <Spinner />;
  }
  if (!!error) {
    return <div>{JSON.stringify(error)}</div>;
  }

  const checkedAllValue = checkedAll(Object.keys(value));

  const checkStatusType = (row) => {
    return (
      (row.status && row.status !== "sent_back_to_customer") ||
      row.quantity === 0
    );
  };

  return (
    <div className="col-12 py-2">
      <TableUi
        className={"table-borders"}
        hoverable={false}
        ths={[
          <Checkbox
            checked={checkedAllValue}
            onChange={() => {
              if (checkedAllValue) {
                setChecked([]);
                setValue((prev) => {
                  let obj = { ...prev };
                  Object.keys(obj).forEach((key) => {
                    obj[key]["qty"] = 0;
                  });
                  return obj;
                });
              } else {
                let keys = Object.keys(value).filter(
                  (key) => !checkStatusType(value[key])
                );
                setChecked(keys);
                setValue((prev) => {
                  let obj = { ...prev };
                  keys.forEach((key) => {
                    obj[key]["qty"] = obj[key]["quantity"];
                  });
                  return obj;
                });
              }
            }}
          />,
          "Item",
          "",
          "Quantity",
        ]}
      >
        {Object.entries(value).map(([key, row]) => {
          const disabled_used = row.quantity === 0;
          const checkStatusType =
            (row.status &&
              row.status !== "sent_back_to_customer" &&
              row.quantity === row.used) ||
            row.quantity === 0;
          const valueToShow = row.used > row.quantity ? row.quantity : row.used;
          return (
            <TableUi.tr key={key}>
              <TableUi.td className={"p-left"}>
                <Checkbox
                  // disabled={row.qty + row.used < row.quantity || disabled_used }
                  disabled={checkStatusType}
                  checked={checkedIncluded(key)}
                  onChange={() => {
                    onChangeChecked(key);
                    setValue((prev) => {
                      let updatedValue = { ...prev };
                      if (!checked.includes(key) && row.qty < row.quantity) {
                        updatedValue[key]["qty"] =
                          (updatedValue[key]["qty"] || 0) +
                          (row.quantity - row.used);
                      } else {
                        updatedValue[key]["qty"] = 0;
                      }
                      return updatedValue;
                    });
                  }}
                />
              </TableUi.td>
              <TableUi.td>
                {have_product_reference(row.product_reference) ? (
                  <a
                    className={`text-primary ${row.manual ? "border-bottom border-danger" : ""
                      }`}
                    href={url_to_store_by_reference(
                      order_shop.sms_tag,
                      row.product_reference
                    )}
                    target="_blank"
                  >
                    {row.product_reference}
                  </a>
                ) : (
                  "Service"
                )}
                {checkedIncluded(key) && (
                  <Input
                    type="text"
                    value={row.product_reference}
                    onChange={(e) => {
                      const newReference = e.target.value;
                      setValue((prev) => ({
                        ...prev,
                        [key]: {
                          ...prev[key],
                          product_reference: newReference,
                        },
                      }));
                    }}
                  />
                )}
              </TableUi.td>
              <TableUi.td>{row.product_name}</TableUi.td>
              <TableUi.td>
                <div className="d-flex align-items-center justify-content-end font-medium">
                  {checkStatusType || disabled_used ? (
                    valueToShow
                  ) : (
                    <TextInput
                      // disabled={row.qty + row.used >= row.quantity}
                      min={0}
                      max={row.quantity}
                      type="number"
                      style={{
                        width: 60,
                      }}
                      value={row.qty}
                      onChange={(e) => {
                        let value = +e.target.value;

                        const isQuantityInvalid = (value, row) => {
                          if (value + row.used > row.quantity) {
                            antdMessageError("Return quantity is too high");
                            return true;
                          }
                          if (value < 0) {
                            antdMessageError("Return quantity is too low");
                            return true;
                          }
                          return false;
                        };

                        if (isQuantityInvalid(value, row)) return;

                        setValue((prev) => {
                          let updatedValue = {
                            ...prev,
                            [key]: { ...prev[key], qty: value },
                          };
                          setCheckById(key, value !== 0);
                          return updatedValue;
                        });
                      }}
                    ></TextInput>
                  )}
                  <div className="ml-2">
                    <span>/</span>
                    {+row.quantity}
                  </div>
                </div>
              </TableUi.td>
            </TableUi.tr>
          );
        })}
      </TableUi>
      <div className="pb-4">
        <div className="d-flex justify-content-between align-items-end">
          <div
            style={{
              width: 160,
            }}
          >
            <InputSwitchComponent
              label="Apply return to"
              name="return_type"
              options={[
                {
                  label: "Logistics",
                  value: "logistics",
                },
                {
                  label: "RMA",
                  value: "rma",
                },
              ]}
              value={returnType}
              onChange={(e) => setReturnType(e.target.value)}
            />
          </div>
          <div>
            <ButtonSecondary
              style={{
                width: 120,
              }}
              disabled={
                !checked.length || mutationCreate.isLoading || !returnType
              }
              onClick={() => mutationCreate.mutate()}
            >
              <IconRollBack />
              <span className="pl-2 text-sm">Return</span>
            </ButtonSecondary>{" "}
          </div>
        </div>
        {!returnType && <Error value={"Logistics/RMA not selected"} />}
      </div>

      <div className="bg-light-primary rounded">
        <TableUi ths={["Returning items", "Quantity", ""]}>
          {order_returns.length == 0 ? (
            <TableUi.tr>
              <TableUi.td className={"p-left"} colspan={3}>
                No data
              </TableUi.td>
            </TableUi.tr>
          ) : (
            order_returns
              .filter((row) => row.qty !== null)
              .map((row) => {
                console.log(row, "row");
                return (
                  <TableUi.tr key={row.id} className={"font-medium"}>
                    <TableUi.td className={"p-left"}>
                      <OrderRowReference
                        order_shop={order_shop}
                        order_row={row}
                      />
                    </TableUi.td>
                    <TableUi.td>{+row.qty}</TableUi.td>
                    <TableUi.td>
                      <button
                        className="btn-white"
                        disabled={mutationDelete.isLoading}
                        onClick={() => mutationDelete.mutate(row.id)}
                      >
                        <IconDelete color="#FF9E00" />
                      </button>
                    </TableUi.td>
                  </TableUi.tr>
                );
              })
          )}
        </TableUi>
      </div>
      {/*  */}
    </div>
  );
};

const OrderRowsReturnInitial = memo(
  ({ order_rows, order_shop, order_id, total_shipping_tax_incl }) => {
    return (
      <QueryClientProvider client={queryOrderRowsReturnClient}>
        <OrderRowsReturn
          {...{ order_rows, order_shop, order_id, total_shipping_tax_incl }}
        />
      </QueryClientProvider>
    );
  }
);

export default OrderRowsReturnInitial;
